// ============================================
// useSEO — per-route document head management (dependency-free).
// ============================================
// CRA renders a single index.html for every route, so without this every page
// would share one title/description — bad for search ranking. This hook updates
// the title, meta description, canonical URL and Open Graph / Twitter tags for
// the current page. Googlebot executes JS and picks up these values.
//
// Usage (mirrors usePageView):
//   import { useSEO } from '../hooks/useSEO';
//   useSEO({ title: '...', description: '...', path: '/about' });
// ============================================

import { useEffect } from 'react';

export const SITE_URL = 'https://vastavik.org';
const DEFAULT_IMAGE = `${SITE_URL}/vastavik.svg`;

export interface SEOOptions {
    title: string;
    description: string;
    /** Route path beginning with "/" (used to build the canonical URL). */
    path: string;
    keywords?: string;
    /** Set true for pages that should not be indexed (e.g. dynamic verify URLs). */
    noindex?: boolean;
    /** Absolute or root-relative image URL for social previews. */
    image?: string;
    /** Canonical override (defaults to SITE_URL + path). Useful for verify pages. */
    canonicalPath?: string;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
    let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

export function useSEO(options: SEOOptions): void {
    const { title, description, path, keywords, noindex, image, canonicalPath } = options;

    useEffect(() => {
        const canonical = `${SITE_URL}${canonicalPath ?? path}`;
        const ogImage = image
            ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
            : DEFAULT_IMAGE;

        document.title = title;
        upsertMeta('name', 'description', description);
        if (keywords) upsertMeta('name', 'keywords', keywords);
        upsertMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
        upsertLink('canonical', canonical);

        // Open Graph
        upsertMeta('property', 'og:title', title);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:url', canonical);
        upsertMeta('property', 'og:image', ogImage);
        upsertMeta('property', 'og:type', 'website');
        upsertMeta('property', 'og:site_name', 'Vastavik');

        // Twitter
        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:title', title);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'twitter:image', ogImage);
    }, [title, description, path, keywords, noindex, image, canonicalPath]);
}

export default useSEO;
